import React, { useEffect, useRef, useState } from 'react';
import { Modal, Button, Flex, Input, Form, Select, Rate, ConfigProvider, Space, theme, Upload, Image, Popconfirm, Typography } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import Editor from './Editor';
import LinkAlert from './LinkAlert';
import TaskTag from './TaskTag';
import { taskPriorityIcons, taskNumberPriorityIcons } from './taskPriority';
import {
    useCreateTaskMutation,
    useAddTaskImageMutation,
    useUpdateTaskMutation,
    useUpdateTagsMutation,
    useUpdatePriorityMutation,
    useAddTaskLinkMutation,
} from '../store/tasks';
import { getNotificationInstance } from '../utils/notification';
import {
    canonicalLink,
    extractUrlsFromHtml,
    extractUrlsFromText,
    normalizeUrl,
    uniqueNormalizedUrls,
} from '../utils/links';
import { MAX_TASK_IMAGES, MAX_TASK_VIDEOS, SUPPORT_TAGS } from '../constants';
import {
    MEDIA_ACCEPT,
    isVideoFile,
    stripMediaBySrc,
    validateTaskMediaFile,
} from '../utils/media';
import type { RootState } from '../store';

const TAG_OPTIONS = Object.values(SUPPORT_TAGS).map((tag) => ({
    label: tag,
    value: tag,
}));

const URL_PATTERN = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/i;

const { Text } = Typography;

type PendingFile = {
    id: string;
    file: File;
    previewUrl: string;
};

interface CreateTaskModalProps {
    open: boolean;
    todoId: number;
    onClose: () => void;
}

interface CreateTaskFormValues {
    taskName: string;
    description?: string;
    tags?: string[];
    priority?: number;
}

/** Пустой редактор TipTap отдаёт <p></p> — считаем это пустым описанием */
const normalizeContent = (html?: string): string => {
    if (!html) {
        return '';
    }
    const plain = html.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();
    return plain ? html : '';
};

const resolvePublicPath = (imagePath: string): string => {
    if (!imagePath) {
        return imagePath;
    }
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://') || imagePath.startsWith('blob:')) {
        return imagePath;
    }
    if (imagePath.startsWith('/public/')) {
        return `/api${imagePath}`;
    }
    if (imagePath.startsWith('public/')) {
        return `/api/${imagePath}`;
    }
    return imagePath;
};

const CreateTaskModal: React.FC<CreateTaskModalProps> = ({ open, todoId, onClose }) => {
    const [form] = Form.useForm<CreateTaskFormValues>();
    const [loading, setLoading] = useState(false);
    const [links, setLinks] = useState<string[]>([]);
    const [linkDraft, setLinkDraft] = useState('');
    const taskNameValue = Form.useWatch('taskName', form) || '';
    const descriptionValue = Form.useWatch('description', form) || '';
    const extractedLinks = uniqueNormalizedUrls(
        extractUrlsFromText(taskNameValue),
        extractUrlsFromHtml(descriptionValue),
    );
    const displayLinks = uniqueNormalizedUrls(links, extractedLinks);
    const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
    const pendingFilesRef = useRef<PendingFile[]>([]);
    pendingFilesRef.current = pendingFiles;

    const settings = useSelector((state: RootState) => state.settings);
    const { token } = theme.useToken();
    const { colorTextSecondary, colorBorderSecondary, colorPrimary } = token;
    const pendingPhotos = pendingFiles.filter((item) => !isVideoFile(item.file));
    const pendingVideos = pendingFiles.filter((item) => isVideoFile(item.file));
    const imageCount = pendingPhotos.length;
    const videoCount = pendingVideos.length;
    const canAddImage = imageCount < MAX_TASK_IMAGES;
    const canAddVideo = videoCount < MAX_TASK_VIDEOS;
    const canAddFile = canAddImage || canAddVideo;

    const [createTask] = useCreateTaskMutation();
    const [addTaskImage] = useAddTaskImageMutation();
    const [updateTask] = useUpdateTaskMutation();
    const [updateTags] = useUpdateTagsMutation();
    const [updatePriority] = useUpdatePriorityMutation();
    const [addTaskLink] = useAddTaskLinkMutation();

    const revokePendingFiles = (files: PendingFile[]) => {
        files.forEach((item) => {
            if (item.previewUrl.startsWith('blob:')) {
                URL.revokeObjectURL(item.previewUrl);
            }
        });
    };

    const resetLocalState = () => {
        setPendingFiles((prev) => {
            revokePendingFiles(prev);
            return [];
        });
        setLinks([]);
        setLinkDraft('');
        setLoading(false);
    };

    useEffect(() => {
        if (open) {
            form.resetFields();
            resetLocalState();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, form]);

    useEffect(() => () => {
        revokePendingFiles(pendingFilesRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getPendingMediaCounts = () => {
        const files = pendingFilesRef.current;
        return {
            imageCount: files.filter((item) => !isVideoFile(item.file)).length,
            videoCount: files.filter((item) => isVideoFile(item.file)).length,
        };
    };

    const handleUploadImageForEditor = async (file: File): Promise<string | null> => {
        const error = validateTaskMediaFile(file, getPendingMediaCounts());
        if (error) {
            getNotificationInstance()?.error({
                message: 'Ошибка',
                description: error,
            });
            return null;
        }

        const url = URL.createObjectURL(file);
        setPendingFiles((prev) => [
            ...prev,
            {
                id: `${file.name}-${file.size}-${Date.now()}`,
                file,
                previewUrl: url,
            },
        ]);
        return url;
    };

    const handleAttachFile = (file: File) => {
        const notification = getNotificationInstance();
        const error = validateTaskMediaFile(file, getPendingMediaCounts());
        if (error) {
            notification?.error({
                message: 'Ошибка',
                description: error,
            });
            return false;
        }

        setPendingFiles((prev) => [
            ...prev,
            {
                id: `${file.name}-${file.size}-${Date.now()}`,
                file,
                previewUrl: URL.createObjectURL(file),
            },
        ]);
        return false;
    };

    const handleRemovePendingFile = (id: string) => {
        setPendingFiles((prev) => {
            const item = prev.find((file) => file.id === id);
            if (!item) {
                return prev;
            }

            const html = form.getFieldValue('description') || '';
            if (item.previewUrl && html.includes(item.previewUrl)) {
                form.setFieldValue(
                    'description',
                    stripMediaBySrc(html, item.previewUrl),
                );
            }

            if (item.previewUrl.startsWith('blob:')) {
                URL.revokeObjectURL(item.previewUrl);
            }
            return prev.filter((file) => file.id !== id);
        });
    };

    const handleAddLink = () => {
        const notification = getNotificationInstance();
        const url = normalizeUrl(linkDraft);
        if (!url || !URL_PATTERN.test(url)) {
            notification?.error({
                message: 'Ошибка',
                description: 'Введена некорректная ссылка. Пожалуйста, проверьте формат URL.',
            });
            return;
        }
        if (links.includes(url)) {
            setLinkDraft('');
            return;
        }
        setLinks((prev) => [...prev, url]);
        setLinkDraft('');
    };

    const handleSubmit = async (values: CreateTaskFormValues) => {
        const taskName = values.taskName.trim();
        if (!taskName) {
            return;
        }

        setLoading(true);
        try {
            let content = normalizeContent(values.description);
            const tags = (values.tags || []).map((tag) => tag.trim()).filter(Boolean);
            const priority = values.priority || 0;

            const created = await createTask({
                todoId,
                taskName,
                taskContent: content,
            }).unwrap();

            const taskId = Number(created?.message?.taskId);
            if (!taskId) {
                return;
            }

            let nextContent = content;
            if (pendingFiles.length > 0) {
                for (const item of pendingFiles) {
                    const result = await addTaskImage({
                        todoId,
                        taskId,
                        file: item.file,
                    }).unwrap();
                    const path = result?.message?.imagePath;
                    if (!path) {
                        continue;
                    }
                    const publicPath = resolvePublicPath(path);
                    if (nextContent.includes(item.previewUrl)) {
                        nextContent = nextContent.split(item.previewUrl).join(publicPath);
                    }
                }

                if (nextContent !== content) {
                    await updateTask({
                        todoId,
                        taskId,
                        taskContent: nextContent,
                    }).unwrap();
                }
            }

            if (tags.length > 0) {
                await updateTags({ todoId, taskId, tags }).unwrap();
            }

            if (priority > 0) {
                await updatePriority({ todoId, taskId, priority }).unwrap();
            }

            for (const link of uniqueNormalizedUrls(links, extractUrlsFromText(taskName), extractUrlsFromHtml(nextContent))) {
                await addTaskLink({ todoId, taskId, link }).unwrap();
            }

            form.resetFields();
            resetLocalState();
            onClose();
        } catch {
            // Ошибка уже показана в baseQuery
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title="Новая задача"
            open={open}
            onCancel={loading ? undefined : onClose}
            footer={null}
            width={720}
            destroyOnHidden
            maskClosable={!loading}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                requiredMark={false}
                initialValues={{ description: '', tags: [], priority: 0 }}
            >
                <Form.Item
                    label="Название"
                    name="taskName"
                    rules={[
                        { required: true, message: 'Введите название задачи' },
                        { max: 500, message: 'Не больше 500 символов' },
                    ]}
                >
                    <Input
                        placeholder="Краткий заголовок…"
                        maxLength={500}
                        autoFocus
                        count={{ show: true, max: 500 }}
                    />
                </Form.Item>

                <Form.Item
                    label="Описание"
                    name="description"
                >
                    <Editor
                        showActions={false}
                        className="task-editor--form"
                        onUploadImage={handleUploadImageForEditor}
                        disableImageUpload={!canAddImage}
                        disableVideoUpload={!canAddVideo}
                    />
                </Form.Item>

                <Form.Item
                    label="Теги"
                    name="tags"
                >
                    <Select
                        mode="tags"
                        allowClear
                        placeholder="Выберите или введите теги"
                        options={TAG_OPTIONS}
                        tokenSeparators={[',']}
                        tagRender={(props) => (
                            <span style={{ marginInlineEnd: 4 }}>
                                <TaskTag
                                    tag={props.value}
                                    closable={props.closable}
                                    onClose={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        props.onClose();
                                    }}
                                />
                            </span>
                        )}
                    />
                </Form.Item>

                <ConfigProvider
                    theme={{
                        components: {
                            Rate: {
                                starBg: settings.theme?.isDark
                                    ? 'rgba(255, 255, 255, 0.35)'
                                    : colorTextSecondary,
                            },
                        },
                    }}
                >
                    <Form.Item
                        label="Приоритет"
                        name="priority"
                    >
                        <Rate
                            className="task-priority-rate"
                            count={5}
                            allowClear
                            character={({ index = 0 }) => (
                                settings.priorityNumberIcons
                                    ? taskNumberPriorityIcons[index + 1]
                                    : taskPriorityIcons[index + 1]
                            )}
                        />
                    </Form.Item>
                </ConfigProvider>

                <Form.Item label="Ссылки">
                    <Flex vertical gap={8}>
                        <Flex gap={8}>
                            <Input
                                value={linkDraft}
                                placeholder="https://…"
                                onChange={(event) => setLinkDraft(event.target.value)}
                                onPressEnter={(event) => {
                                    event.preventDefault();
                                    handleAddLink();
                                }}
                                disabled={loading}
                            />
                            <Button
                                icon={<PlusOutlined />}
                                onClick={handleAddLink}
                                disabled={loading || !linkDraft.trim()}
                            >
                                Добавить
                            </Button>
                        </Flex>
                        {displayLinks.length > 0 && (
                            <Space align="center" wrap size={8}>
                                {displayLinks.map((url) => (
                                    <LinkAlert
                                        key={url}
                                        url={url}
                                        onDelete={
                                            links.some((item) => canonicalLink(item) === canonicalLink(url))
                                                ? () => setLinks((prev) => prev.filter((item) => canonicalLink(item) !== canonicalLink(url)))
                                                : undefined
                                        }
                                    />
                                ))}
                            </Space>
                        )}
                    </Flex>
                </Form.Item>

                <Form.Item
                    label={
                        <span>
                            Файлы
                            <Text type="secondary" style={{ marginLeft: 8, fontWeight: 400 }}>
                                {imageCount}/{MAX_TASK_IMAGES} фото · {videoCount}/{MAX_TASK_VIDEOS} видео
                            </Text>
                        </span>
                    }
                >
                    <Flex vertical gap={8}>
                        {pendingVideos.map((item) => (
                            <div key={item.id} style={{ position: 'relative' }}>
                                <video
                                    className="task-file-video"
                                    src={item.previewUrl}
                                    controls
                                    preload="metadata"
                                    playsInline
                                    style={{
                                        width: '100%',
                                        maxHeight: 220,
                                        borderRadius: 8,
                                        background: '#000',
                                        border: `1px solid ${colorBorderSecondary}`,
                                    }}
                                />
                                <Popconfirm
                                    title="Удалить видео?"
                                    okText="Удалить"
                                    cancelText="Отмена"
                                    onConfirm={() => handleRemovePendingFile(item.id)}
                                >
                                    <Button
                                        icon={<DeleteOutlined />}
                                        size="small"
                                        disabled={loading}
                                        style={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 8,
                                        }}
                                    />
                                </Popconfirm>
                            </div>
                        ))}
                        <Image.PreviewGroup>
                            <Flex wrap="wrap" gap={8}>
                                {pendingPhotos.map((item) => (
                                    <div key={item.id} style={{ position: 'relative' }}>
                                        <Image
                                            width={100}
                                            height={100}
                                            src={item.previewUrl}
                                            style={{
                                                objectFit: 'cover',
                                                borderRadius: 8,
                                                border: `1px solid ${colorBorderSecondary}`,
                                            }}
                                        />
                                        <Popconfirm
                                            title="Удалить изображение?"
                                            okText="Удалить"
                                            cancelText="Отмена"
                                            onConfirm={() => handleRemovePendingFile(item.id)}
                                        >
                                            <Button
                                                icon={<DeleteOutlined />}
                                                size="small"
                                                disabled={loading}
                                                style={{
                                                    position: 'absolute',
                                                    top: 4,
                                                    right: 4,
                                                }}
                                            />
                                        </Popconfirm>
                                    </div>
                                ))}

                                {canAddFile && (
                                    <Upload
                                        className="task-upload-tile"
                                        listType="picture-card"
                                        showUploadList={false}
                                        beforeUpload={handleAttachFile}
                                        accept={MEDIA_ACCEPT}
                                        maxCount={1}
                                        disabled={loading}
                                        style={{ ['--task-upload-border' as string]: colorPrimary } as React.CSSProperties}
                                    >
                                        <PlusOutlined />
                                    </Upload>
                                )}
                            </Flex>
                        </Image.PreviewGroup>
                    </Flex>
                </Form.Item>

                <Form.Item style={{ marginBottom: 0 }}>
                    <Flex gap="middle" justify="end">
                        <Button onClick={onClose} disabled={loading}>
                            Отмена
                        </Button>
                        <Button type="primary" htmlType="submit" loading={loading}>
                            Добавить
                        </Button>
                    </Flex>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default CreateTaskModal;
